import { useState, useEffect, useRef, useCallback } from "react";

interface UseDynamicTruncateOptions {
  /** Font size in pixels (default: 14 for text-sm) */
  fontSize?: number;
  /** Font family (default: system font stack) */
  fontFamily?: string;
  /** Font weight (default: 400) */
  fontWeight?: number;
  /** Padding to account for (default: 0) */
  padding?: number;
  /** Minimum characters to show (default: 10) */
  minChars?: number;
  /** Maximum characters to show (default: Infinity) */
  maxChars?: number;
  /** Use this fraction of calculated capacity, 0–1 (default: 0.9). Higher = more characters shown. */
  capacityFactor?: number;
}

/**
 * Hook that calculates the maximum number of characters that can fit
 * in a container based on its width. Uses ResizeObserver to track size changes.
 */
export function useDynamicTruncate(options: UseDynamicTruncateOptions = {}) {
  const {
    fontSize = 14,
    fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight = 400,
    padding = 0,
    minChars = 10,
    maxChars = Infinity,
    capacityFactor = 0.9,
  } = options;

  const [maxLength, setMaxLength] = useState<number>(50); // Default fallback
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  // Calculate character width using a hidden measurement element
  const calculateMaxChars = useCallback((containerWidth: number) => {
    if (!measureRef.current || containerWidth <= 0) {
      return Math.max(minChars, Math.min(50, maxChars));
    }

    // Create a temporary element to measure character width
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      return Math.max(minChars, Math.min(50, maxChars));
    }

    context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    const avgCharWidth = context.measureText("M").width; // 'M' is typically the widest character
    const narrowCharWidth = context.measureText("i").width; // 'i' is typically narrow
    const avgWidth = (avgCharWidth + narrowCharWidth) / 2;

    // Calculate available width (accounting for padding)
    const availableWidth = containerWidth - padding;
    
    // Calculate max characters (capacityFactor for safety margin; higher = use more space)
    const calculated = Math.floor((availableWidth / avgWidth) * capacityFactor);
    
    // Clamp between min and max
    const clamped = Math.max(minChars, Math.min(calculated, maxChars));
    
    return clamped;
  }, [fontSize, fontFamily, fontWeight, padding, minChars, maxChars, capacityFactor]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      // If no container yet, set a default and check again soon
      const checkInterval = setInterval(() => {
        if (containerRef.current) {
          clearInterval(checkInterval);
          // Trigger re-measurement by updating a dummy state
          setMaxLength(prev => prev);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    // Initial calculation
    const updateMaxLength = () => {
      const currentContainer = containerRef.current;
      if (!currentContainer) return;
      const width = currentContainer.offsetWidth;
      if (width > 0) {
        const newMaxLength = calculateMaxChars(width);
        setMaxLength(newMaxLength);
      }
    };

    // Initial calculation with a small delay to ensure layout is complete
    const timeoutId = setTimeout(updateMaxLength, 100);

    // Use ResizeObserver to track size changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          const newMaxLength = calculateMaxChars(width);
          setMaxLength(newMaxLength);
        }
      }
    });

    resizeObserver.observe(container);

    // Also listen to window resize as fallback
    window.addEventListener("resize", updateMaxLength);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateMaxLength);
    };
  }, [calculateMaxChars]);

  return {
    maxLength,
    containerRef,
    measureRef,
  };
}

/**
 * Helper function to truncate text based on max length
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength);
}
